import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenBackdrop } from './ScreenBackdrop';
import { PinScreen } from './PinScreen';
import { ChevronRight, CopyIcon, WarningIcon } from './icons';
import { clearLocalSeed, fetchRemoteWallet, readSeed } from '../../lib/wallet';
import { isWebSession } from '../../lib/webSession';
import { reauthApi, deleteWalletApi, startWalletResetApi } from '../../lib/me';
import { runBiometric } from '../../lib/biometric';
import { setLockFlag } from '../../lib/lock';
import { markWalletSetupOffer } from '../../lib/walletSetupOffer';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';

type Step = 'auth' | 'warn' | 'reveal' | 'reset-pin' | 'reset-code';

export function SeedPhraseFlow({
  pinSet,
  biometricEnabled,
  emailVerified,
  email,
  initData,
  onClose,
}: {
  pinSet: boolean;
  biometricEnabled: boolean;
  emailVerified: boolean;
  email: string | null;
  initData: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();

  const needAuth = pinSet || biometricEnabled;
  const [step, setStep] = useState<Step>(needAuth ? 'auth' : 'warn');
  const [pinError, setPinError] = useState(false);
  const [words, setWords] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [bioTried, setBioTried] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [seedProblem, setSeedProblem] = useState<'network' | 'gone' | null>(null);
  const [remoteMissing, setRemoteMissing] = useState<boolean | null>(null);
  const [resetCode, setResetCode] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const tokenRef = useRef<string | undefined>(undefined);
  const resetTokenRef = useRef<string | undefined>(undefined);
  const web = isWebSession();

  const notify = (msg: string) => {
    const wa = window.Telegram?.WebApp;
    if (wa?.showAlert) wa.showAlert(msg);
    else window.alert(msg);
  };

  useEffect(() => {
    if (!web || seedProblem !== 'gone') return;
    let cancel = false;
    void fetchRemoteWallet(initData)
      .then((wallet) => {
        if (!cancel) setRemoteMissing(wallet === null);
      })
      .catch(() => {
        if (!cancel) setSeedProblem('network');
      });
    return () => {
      cancel = true;
    };
  }, [web, seedProblem, initData]);

  // Try biometric automatically while the PIN screen is also available.
  // Only for seed reveal — never auto-approve wallet reset.
  useEffect(() => {
    if (step !== 'auth' || !biometricEnabled || bioTried) return;
    setBioTried(true);
    void (async () => {
      const ok = await runBiometric(t('profile.seedReason'));
      if (!ok) return;
      const r = await reauthApi(initData, { method: 'biometric' });
      if (r.ok) {
        tokenRef.current = r.token;
        setStep('warn');
      }
      // else: locked / failed → fall back to PIN entry
    })();
  }, [step, biometricEnabled, bioTried, initData, t]);

  const onPin = async (pin: string) => {
    const r = await reauthApi(initData, { method: 'pin', pin });
    if (r.ok) {
      tokenRef.current = r.token;
      setPinError(false);
      setStep('warn');
    } else {
      // Wrong PIN or server-enforced lockout — both block the reveal.
      setPinError(true);
    }
  };

  const reveal = async () => {
    const read = await readSeed(initData, tokenRef.current);
    if (read.ok) {
      setSeedProblem(null);
      setRemoteMissing(null);
      setWords(read.mnemonic.trim().split(/\s+/));
    } else {
      setWords([]);
      setRemoteMissing(null);
      setSeedProblem(read.reason === 'network' || read.reason === 'reauth' ? 'network' : 'gone');
    }
    setStep('reveal');
  };

  const openWalletSetup = async () => {
    await clearLocalSeed();
    location.reload();
  };

  const sendResetCode = async (reauthToken?: string) => {
    setResetBusy(true);
    setResetError(null);
    try {
      const r = await startWalletResetApi(initData, reauthToken);
      setResetEmail(r.email || email || '');
      if (!r.sent) setResetError(t('profile.emailNotSent'));
      setResetCode('');
      setStep('reset-code');
    } catch (e) {
      const msg = String(e);
      if (msg.includes('email_required')) notify(t('profile.resetEmailRequired'));
      else if (msg.includes('reauth_required')) setStep('reset-pin');
      else notify(t('profile.resetFailed'));
    } finally {
      setResetBusy(false);
    }
  };

  const beginReset = () => {
    if (!emailVerified || !email) {
      notify(t('profile.resetEmailRequired'));
      return;
    }
    const wa = window.Telegram?.WebApp;
    const go = () => {
      setResetError(null);
      setResetCode('');
      // Fresh PIN for reset — do not reuse Face ID / reveal reauth token.
      if (pinSet) {
        resetTokenRef.current = undefined;
        setPinError(false);
        setStep('reset-pin');
        return;
      }
      void sendResetCode(undefined);
    };
    if (wa?.showConfirm) wa.showConfirm(t('profile.resetWalletConfirm'), (ok) => ok && go());
    else if (window.confirm(t('profile.resetWalletConfirm'))) go();
  };

  const onResetPin = async (pin: string) => {
    const r = await reauthApi(initData, { method: 'pin', pin });
    if (!r.ok) {
      setPinError(true);
      return;
    }
    resetTokenRef.current = r.token;
    setPinError(false);
    await sendResetCode(r.token);
  };

  const confirmReset = async () => {
    if (!/^\d{6}$/.test(resetCode)) {
      setResetError(t('profile.codeInvalid'));
      return;
    }
    setResetBusy(true);
    setResetError(null);
    try {
      await deleteWalletApi(initData, resetCode, resetTokenRef.current);
      markWalletSetupOffer();
      await clearLocalSeed();
      setLockFlag(false);
      location.reload();
    } catch (e) {
      const msg = String(e);
      if (msg.includes('wrong_code')) setResetError(t('profile.codeWrong'));
      else if (msg.includes('code_expired') || msg.includes('no_reset_code'))
        setResetError(t('profile.resetCodeExpired'));
      else if (msg.includes('too_many_attempts')) setResetError(t('profile.resetCodeLocked'));
      else if (msg.includes('reauth_required')) {
        setStep('reset-pin');
        setResetError(null);
      } else setResetError(t('profile.resetFailed'));
    } finally {
      setResetBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(words.join(' ')).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  if (step === 'auth' || step === 'reset-pin') {
    const resetPin = step === 'reset-pin';
    return (
      <PinScreen
        key={pinError ? `${step}-err` : `${step}-ok`}
        title={
          pinError
            ? `${t('profile.enterPin')} · ${t('profile.pinTryAgain')}`
            : resetPin
              ? t('profile.resetEnterPin')
              : t('profile.enterPin')
        }
        onComplete={resetPin ? onResetPin : onPin}
        onClose={resetPin ? () => setStep('reveal') : onClose}
      />
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-surface">
      <ScreenBackdrop height={320} fadeStart={26} />
      <div className="relative z-10 flex min-h-screen flex-col px-4 pb-[calc(var(--tg-safe-bottom)_+_2rem)] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <header className="flex items-center gap-3 py-2">
          {!tgBack && (
            <button
              onClick={step === 'reset-code' ? () => setStep('reveal') : onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
              aria-label="Back"
            >
              <ChevronRight size={16} className="rotate-180 text-ink" />
            </button>
          )}
          <h1 className="text-[20px] font-medium text-ink">
            {step === 'reset-code' ? t('profile.resetCodeTitle') : t('profile.seedPhrase')}
          </h1>
        </header>

        {step === 'reset-code' ? (
          <div className="flex flex-1 flex-col">
            <div className="mt-6 flex flex-col items-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#fdecec]">
                <WarningIcon size={30} className="text-[#ff3b30]" />
              </span>
              <p className="mt-5 max-w-[20rem] text-[14px] leading-relaxed text-muted">
                {t('profile.resetCodeSent', { email: resetEmail || email || '' })}
              </p>
            </div>
            <div className="mt-8">
              <input
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                className="w-full rounded-[16px] bg-white px-4 py-3.5 text-center text-[20px] tracking-[8px] text-ink outline-none shadow-card"
              />
              {resetError && <p className="mt-3 text-center text-[13px] text-[#ff3b30]">{resetError}</p>}
            </div>
            <div className="flex-1" />
            <button
              onClick={() => void confirmReset()}
              disabled={resetBusy || resetCode.length !== 6}
              className="flex h-14 items-center justify-center rounded-full bg-[#ff3b30] text-[15px] font-medium text-white disabled:opacity-40 active:opacity-90"
            >
              {resetBusy ? '…' : t('profile.resetCodeConfirm')}
            </button>
            <button
              onClick={() => void sendResetCode(resetTokenRef.current)}
              disabled={resetBusy}
              className="mt-3 flex h-12 items-center justify-center text-[14px] font-medium text-muted active:opacity-70 disabled:opacity-40"
            >
              {t('profile.resetResend')}
            </button>
          </div>
        ) : step === 'warn' ? (
          <div className="flex flex-1 flex-col">
            <div className="mt-6 flex flex-col items-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#fdecec]">
                <WarningIcon size={30} className="text-[#ff3b30]" />
              </span>
              <h2 className="mt-5 text-[20px] font-semibold text-ink">{t('profile.seedWarnTitle')}</h2>
              <p className="mt-3 max-w-[20rem] text-[14px] leading-relaxed text-muted">
                {t('profile.seedWarnBody')}
              </p>
            </div>
            <div className="flex-1" />
            <label className="mb-4 flex items-start gap-3 rounded-[18px] bg-white p-4 text-left active:bg-black/[0.02]">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#377dff]"
              />
              <span className="text-[13px] leading-relaxed text-muted">{t('profile.seedAccept')}</span>
            </label>
            <button
              onClick={reveal}
              disabled={!accepted}
              className={`flex h-14 items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white transition-opacity ${
                accepted ? 'active:opacity-90' : 'pointer-events-none opacity-40'
              }`}
            >
              {t('profile.reveal')}
            </button>
          </div>
        ) : words.length === 0 ? (
          <div className="flex flex-1 flex-col">
            <div className="mt-16 flex flex-col items-center text-center">
              <p className="max-w-[18rem] text-[14px] text-muted">
                {web && seedProblem === 'gone'
                  ? remoteMissing === null
                    ? t('common.loading')
                    : remoteMissing
                      ? t('profile.seedMissingWeb')
                      : t('profile.seedGoneWeb')
                  : t('profile.seedUnavailable')}
              </p>
            </div>
            <div className="flex-1" />
            {web && seedProblem === 'gone' && remoteMissing ? (
              <button
                onClick={() => void openWalletSetup()}
                className="flex h-14 items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90"
              >
                {t('profile.setupWallet')}
              </button>
            ) : web && seedProblem === 'network' ? (
              <button
                onClick={() => void reveal()}
                className="flex h-14 items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90"
              >
                {t('common.retry')}
              </button>
            ) : web && seedProblem === 'gone' && remoteMissing === null ? null : (
              <button
                onClick={beginReset}
                disabled={resetBusy}
                className="flex h-14 items-center justify-center rounded-full border border-[#ff3b30] text-[15px] font-medium text-[#ff3b30] active:opacity-80 disabled:opacity-40"
              >
                {t('profile.resetWallet')}
              </button>
            )}
          </div>
        ) : (
          <div className="mt-4 flex flex-1 flex-col">
            <div className="grid grid-cols-2 gap-2.5">
              {words.map((w, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-[14px] bg-white px-3 py-3 shadow-card"
                >
                  <span className="w-5 text-[13px] tabular-nums text-muted">{i + 1}</span>
                  <span className="text-[15px] font-medium text-ink">{w}</span>
                </div>
              ))}
            </div>
            <button
              onClick={copy}
              className="mt-6 flex h-14 items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90"
            >
              <CopyIcon size={18} className="text-white" />
              {copied ? t('profile.seedCopied') : t('profile.seedCopy')}
            </button>
            <button
              onClick={beginReset}
              disabled={resetBusy}
              className="mt-3 flex h-12 items-center justify-center text-[14px] font-medium text-[#ff3b30] active:opacity-70 disabled:opacity-40"
            >
              {t('profile.resetWallet')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
